// A saved payment card as `<ns-payment-card>` and the automatic payments lines show it, from what the plAutoPayment row
// stores (Stripe's lowercase `card.brand`, the last 4 and the expiry). Pure helpers, so they're unit tested.

// The brands with an official logo in public/images/icons/ (`<brand>-logo.svg`, served at /images/icons/); any other
// brand shows its name in the logo's place
export const CARD_LOGOS = ['visa', 'mastercard'] as const;

export type CardLogo = (typeof CARD_LOGOS)[number];

// The brands' own names where capitalising Stripe's value gets them wrong
const BRAND_NAMES: Record<string, string> = {
  amex: 'American Express',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  eftpos_au: 'eftpos Australia',
};

const isWhole = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

const brandKey = (brand: string | null | undefined): string => (brand ?? '').trim().toLowerCase();

// The logo file's brand ('visa' for /images/icons/visa-logo.svg), or null when there's no file for it
export function cardLogo(brand: string | null | undefined): CardLogo | null {
  const key = brandKey(brand);
  return (CARD_LOGOS as readonly string[]).includes(key) ? (key as CardLogo) : null;
}

// "Visa", "Mastercard", "American Express": the brand as people read it, or null with no usable brand
export function cardBrandName(brand: string | null | undefined): string | null {
  const key = brandKey(brand);
  if (!key || key === 'unknown') return null;
  return BRAND_NAMES[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

// "04/2030": the expiry as MM/YYYY, or null unless both the month and the year are whole numbers
export function cardExpiry(
  card: { cardExpMonth?: number | null; cardExpYear?: number | null } | null | undefined,
): string | null {
  const month = card?.cardExpMonth;
  const year = card?.cardExpYear;
  if (!isWhole(month) || !isWhole(year)) return null;
  return `${String(month).padStart(2, '0')}/${year}`;
}
